import { useEffect, useRef, useState } from "react"
import { useFilterQueryParams } from "../../../hooks/filter_query_params"
import { useMapViewData } from "../../../hooks/map_view_data"
import {
  BaseLayer,
  ALWAYS_SHOW_FILTER,
  getFirstSymbolLayerId,
} from "./base_layer"
import {
  FLOW_CONSTANTS,
  DASH_BUCKETS,
  buildDashExpression,
} from "../../../utils/flowUtils"
import { FlatMapKeys } from "./flat_source"

export const CampusFlowKeys = {
  SOURCE_ID: "da-campus-flow-source",
  Layers: {
    FLOW_LINES: "da-campus-flow-lines",
  },
}

export class FlowLineLayer extends BaseLayer {
  constructor(sourceId, layerId) {
    super(
      layerId,
      "line",
      sourceId,
      {
        "line-color": FLOW_CONSTANTS.COLOR,
        "line-opacity": FLOW_CONSTANTS.OPACITY,
        "line-width": [
          "interpolate", ["linear"], ["get", "normalizedVolume"],
          0, FLOW_CONSTANTS.MIN_WIDTH,
          1, FLOW_CONSTANTS.MAX_WIDTH,
        ],
        "line-dasharray": buildDashExpression(DASH_BUCKETS),
      },
      ALWAYS_SHOW_FILTER
    )
  }
}

const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] }

export function CampusFlowSource({ map, isMapLoading }) {
  const { campusFlowContent } = useMapViewData()
  const { campusFlow } = useFilterQueryParams()

  const flowGeoJSON = campusFlowContent?.flowGeojson ?? EMPTY_GEOJSON

  const [isLoaded, setIsLoaded] = useState(false)
  const flowLineLayerRef = useRef(
    new FlowLineLayer(CampusFlowKeys.SOURCE_ID, CampusFlowKeys.Layers.FLOW_LINES)
  )
  const animationRef = useRef(null)

  // Initialize map source and layer
  useEffect(() => {
    if (!map || isMapLoading || isLoaded) return

    if (!map.getSource(CampusFlowKeys.SOURCE_ID)) {
      map.addSource(CampusFlowKeys.SOURCE_ID, {
        type: "geojson",
        data: EMPTY_GEOJSON,
        promoteId: "id",
      })
    }

    const firstSymbolId = getFirstSymbolLayerId(map)
    flowLineLayerRef.current.addToMap(map, firstSymbolId, ALWAYS_SHOW_FILTER)
    setIsLoaded(true)
  }, [map, isMapLoading, isLoaded])

  // Keep flow lines above all polygon/fan/outline layers.
  // Other layers (flat_source) may be added after the flow layer, pushing it down.
  // Re-position on every idle event so ordering stays correct.
  useEffect(() => {
    if (!map || !isLoaded || !campusFlow) return

    function reorderFlowLayer() {
      if (!map.getLayer(CampusFlowKeys.Layers.FLOW_LINES)) return

      // Position flow lines above all CIQ polygon/outline layers.
      // Fan outline is the topmost CIQ layer added by flat_source.
      const layers = map.getStyle().layers
      const fanOutlineIdx = layers.findIndex(l => l.id === FlatMapKeys.Layers.FAN_OUTLINE)
      if (fanOutlineIdx === -1) return // flat_source layers not loaded yet

      const flowIdx = layers.findIndex(l => l.id === CampusFlowKeys.Layers.FLOW_LINES)
      if (flowIdx !== -1 && flowIdx === fanOutlineIdx + 1) return // already correct

      if (fanOutlineIdx + 1 >= layers.length) {
        // Fan outline is the last layer — move flow to top of stack
        map.moveLayer(CampusFlowKeys.Layers.FLOW_LINES)
      } else {
        // Insert flow lines just above fan outline
        const nextLayerId = layers[fanOutlineIdx + 1].id
        map.moveLayer(CampusFlowKeys.Layers.FLOW_LINES, nextLayerId)
      }
    }

    // Position immediately and on every idle (after style/layer changes settle)
    reorderFlowLayer()
    map.on("idle", reorderFlowLayer)

    return () => {
      map.off("idle", reorderFlowLayer)
    }
  }, [map, isLoaded, campusFlow])

  // Toggle layer visibility based on campusFlow toggle
  useEffect(() => {
    if (!map || !isLoaded) return

    flowLineLayerRef.current.setVisibility(map, campusFlow)
  }, [map, isLoaded, campusFlow])

  // Set GeoJSON data when it changes
  useEffect(() => {
    if (!map || !isLoaded) return
    const source = map.getSource(CampusFlowKeys.SOURCE_ID)
    if (source) source.setData(flowGeoJSON)
  }, [map, isLoaded, flowGeoJSON])

  // Drive dash animation using a pre-computed sequence of dasharray values.
  // This is the official Mapbox approach (ant-path pattern). The sequence
  // cycles seamlessly because the last frame connects back to the first.
  useEffect(() => {
    const hasFeatures = flowGeoJSON.features.length > 0
    if (!map || !isLoaded || !campusFlow || !hasFeatures) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
      return
    }

    // Build a seamless dasharray sequence for the largest bucket.
    // Pattern: dash of length D, gap of length G.
    // Phase 1 (steps 0..N-1): shrink leading dash, grow leading zero-gap
    //   [D-i*step, G, i*step]           (3-value)
    // Phase 2 (steps N..2N-1): grow trailing dash, shrink trailing gap
    //   [0, G-(j*step), D, j*step]      (4-value, leading zero)
    // This produces a smooth loop identical to the Mapbox ant-path example.
    const d = DASH_BUCKETS[DASH_BUCKETS.length - 1].dashLength
    const g = DASH_BUCKETS[DASH_BUCKETS.length - 1].gapLength
    const steps = Math.round(d / 0.5) // 0.5 unit increments
    const inc = d / steps
    const sequence = []

    for (let i = 0; i <= steps; i++) {
      sequence.push([d - i * inc, g, i * inc])
    }
    for (let i = 1; i < steps; i++) {
      sequence.push([0, g - i * inc, d, i * inc])
    }

    // Reverse the sequence so dashes flow forward along the line geometry
    // (from origin building toward destination building). Without this,
    // the ant-path pattern animates from destination → origin.
    sequence.reverse()

    let step = 0
    const msPerStep = 80

    function animate(timestamp) {
      const newStep = Math.floor(timestamp / msPerStep) % sequence.length
      if (newStep !== step) {
        step = newStep
        const layer = map.getLayer(CampusFlowKeys.Layers.FLOW_LINES)
        if (layer) {
          map.setPaintProperty(
            CampusFlowKeys.Layers.FLOW_LINES,
            "line-dasharray",
            sequence[step]
          )
        }
      }
      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
    }
  }, [map, isLoaded, campusFlow, flowGeoJSON])

  // Zoom is handled centrally by useMapData — see flowZoomBounds useMemo

  return null
}
